import React from 'react';
import { Eye, Activity, History, Edit2, Link2, KeyRound, PlayCircle, Ban, Trash2, Users } from 'lucide-react';
import { StaffAvatar } from './StaffAvatar';
import { RolePill, StatusPills } from './StaffBadges';
import { relativeTime } from './peopleUtils';

const COLS = 'grid-cols-[auto_2.4fr_1fr_1fr_1.2fr_0.9fr_auto]';

const RowActions = ({ staff, isSelf, actions }) => {
  const list = [
    [Eye, 'quickView', 'Quick view', 'view'], [Activity, 'hrProfile', 'HR profile', 'hr-profile'], [History, 'actionLog', 'Action log', 'action-log'],
    [Edit2, 'edit', 'Edit', 'edit'], [Link2, 'invite', 'Invite link', 'invite'], [KeyRound, 'reset', 'Reset password', 'reset'],
  ];
  if (!isSelf && !staff.is_protected) {
    list.push(staff.is_suspended ? [PlayCircle, 'unsuspend', 'Reactivate', 'unsuspend'] : [Ban, 'suspend', 'Suspend', 'suspend']);
    list.push([Trash2, 'delete', 'Delete', 'delete']);
  }
  return (
    <div className="flex items-center gap-0.5 opacity-40 group-hover:opacity-100 transition-opacity">
      {list.map(([Ic, key, title, tid]) => (
        <button key={key} onClick={() => actions[key](staff)} title={title} data-testid={`${tid}-${staff.username}`}
          className={`p-1.5 rounded-lg text-white/50 hover:bg-white/[0.06] transition-colors ${['suspend', 'delete'].includes(key) ? 'hover:text-rose-300' : 'hover:text-cyan-200'}`}>
          <Ic className="w-3.5 h-3.5" />
        </button>
      ))}
    </div>
  );
};

export const StaffTable = ({ rows, total, selected, onToggle, onToggleAll, online, onLeaveMap, isProbation, currentUsername, actions }) => (
  <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden" data-testid="staff-table">
    <div className={`grid ${COLS} gap-4 px-5 py-3 border-b border-white/[0.06] text-[9px] font-semibold text-white/40 uppercase tracking-[0.18em]`}>
      <input type="checkbox" checked={rows.length > 0 && rows.every(r => selected.has(r.username))} onChange={onToggleAll}
        data-testid="select-all-staff" className="w-4 h-4 accent-cyan-400 cursor-pointer" />
      <div>Staff member</div><div>Role</div><div>Department</div><div>Status</div><div>Last active</div><div>Actions</div>
    </div>
    {rows.length === 0 ? (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="no-staff">
        <Users className="w-10 h-10 text-white/20 mb-3" />
        <p className="text-white/50 text-sm">No staff match these filters</p>
      </div>
    ) : (
      <div className="divide-y divide-white/[0.04]">
        {rows.map(s => (
          <div key={s.username} data-testid={`staff-row-${s.username}`}
            className={`group grid ${COLS} gap-4 px-5 py-3 items-center hover:bg-white/[0.025] transition-colors ${selected.has(s.username) ? 'bg-cyan-500/[0.04]' : ''} ${s.is_suspended ? 'opacity-60' : ''}`}>
            <input type="checkbox" checked={selected.has(s.username)} onChange={() => onToggle(s.username)}
              data-testid={`select-staff-${s.username}`} className="w-4 h-4 accent-cyan-400 cursor-pointer" />
            <button onClick={() => actions.quickView(s)} className="flex items-center gap-3 min-w-0 text-left">
              <StaffAvatar staff={s} online={online.has(s.username)} testId={`online-dot-${s.username}`} />
              <div className="min-w-0">
                <p className="text-white text-sm font-medium truncate">{s.display_name || s.username}{s.username === currentUsername && <span className="ml-1.5 text-[9px] text-cyan-300">(you)</span>}</p>
                <p className="text-white/40 text-[11px] truncate">@{s.username}{s.job_title ? ` · ${s.job_title}` : ''}</p>
              </div>
            </button>
            <div><RolePill role={s.role} /></div>
            <p className="text-white/55 text-xs truncate">{s.department || '—'}</p>
            <StatusPills staff={s} onLeave={!!onLeaveMap[s.username]} probation={isProbation(s)} />
            <p className="text-white/40 text-xs">{relativeTime(s.last_login)}</p>
            <RowActions staff={s} isSelf={s.username === currentUsername} actions={actions} />
          </div>
        ))}
      </div>
    )}
    <div className="px-5 py-2.5 border-t border-white/[0.04] text-[11px] text-white/35">Showing {rows.length} of {total} staff</div>
  </div>
);
